"use client";

import { CalendarIcon, CheckIcon } from "@heroicons/react/16/solid";
import { ClockIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { FC } from "react";
import { twMerge } from "tailwind-merge";
import Spinner from "./Spinner";
import type { PublicationStatusKind } from "./publicationFlow/publicationStatusPresentation";

type PublicationStatusIconProps = {
  kind: PublicationStatusKind | "scheduled";
  /** Tooltip and accessible name — single-post or aggregate copy. */
  label: string;
  className?: string;
  /** Colour class from the shared presentation map. */
  iconClassName?: string;
  /**
   * `sm` — calendar tile next to the time (14px; Posted uses its shared 16px token).
   * `md` — larger glyph well when a parent needs more presence.
   */
  size?: "sm" | "md";
  /**
   * When false, render only the coloured glyph (parent owns tooltip /
   * labelling).
   */
  chrome?: boolean;
};

const GLYPH_SIZE = {
  sm: "size-3.5",
  md: "w-4 h-4",
} as const;

const WELL_SIZE = {
  sm: "size-3.5",
  md: "h-7 w-7",
} as const;

/**
 * Icon-only status mark for Calendar tiles (per publication). Tooltip +
 * aria-label carry the word; the glyph stays secondary to the time beside
 * it. Saved scheduled posts reach this component only in the schedule
 * workspace, where the icon distinguishes persisted slots from newly staged
 * ones. The schedule queue uses an aggregate status beside its title.
 */
const PublicationStatusIcon: FC<PublicationStatusIconProps> = ({
  kind,
  label,
  className,
  iconClassName,
  size = "sm",
  chrome = true,
}) => {
  const glyphClass = twMerge(GLYPH_SIZE[size], "shrink-0");
  const glyph = (() => {
    switch (kind) {
      case "published":
        return <CheckIcon className={twMerge(glyphClass, "size-4")} aria-hidden />;
      case "scheduled":
        return <CalendarIcon className={twMerge(glyphClass, "size-4")} aria-hidden />;
      case "failed":
        return <ExclamationCircleIcon className={glyphClass} aria-hidden />;
      case "queued":
        return <ClockIcon className={twMerge(glyphClass, "motion-safe:animate-pulse")} aria-hidden />;
      case "publishing":
      case "verifying":
        return (
          <span
            className={twMerge(glyphClass, "flex items-center justify-center")}
            aria-hidden
          >
            <Spinner className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
          </span>
        );
      default:
        return null;
    }
  })();

  if (!glyph) return null;

  if (!chrome) {
    return (
      <span className={twMerge("inline-flex shrink-0", iconClassName, className)}>
        {glyph}
      </span>
    );
  }

  return (
    <span
      role="img"
      tabIndex={0}
      aria-label={label}
      data-tooltip-id="global"
      data-tooltip-content={label}
      className={twMerge(
        "inline-flex shrink-0 items-center justify-center rounded outline-none focus-visible:ring-2 focus-visible:ring-ring",
        WELL_SIZE[size],
        iconClassName,
        className,
      )}
    >
      {glyph}
    </span>
  );
};

export default PublicationStatusIcon;
